import type { MaybePromise } from '@/type-utils';
import { useCallback, useRef, useState } from 'react';

/**
 * A React hook for managing whether there are pending operations in progress.
 * @returns A tuple containing a boolean of whether there are pending operations,
 * and a function to add a promise-like to the list of pending operations.
 *
 * **Usage Note**: A "pending" operation is an async operation that has not settled yet.
 * If an operation errors, it will still be removed from the list.
 */
export function usePending(): [
  isPending: boolean,
  addPending: (promise: MaybePromise<unknown>) => void
] {
  const countRef = useRef(0);
  const [isPending, setIsPending] = useState(false);

  const addPromise = useCallback((newPromise: MaybePromise<unknown>): void => {
    if (!(newPromise instanceof Promise)) return;

    countRef.current++;
    setIsPending(true);

    const remove = (): void => {
      countRef.current--;
      if (countRef.current === 0) setIsPending(false);
    };

    // eslint-disable-next-line no-void -- stop "tracking" the promise once it settles
    void newPromise.then(remove, remove); // we use `then` instead of `finally` due to a weird interaction with Jest. It's also marginally faster.
  }, []);

  return [isPending, addPromise];
}
